import type { TiebreakerTeam, TiebreakerStepFn, StepResult } from '../types';
import { RankMethod } from '../../enums/rank-method';
import { calcPctNaN, bestOf, addExplanation, addExplanationAll } from '../helpers';

/**
 * NFL Common Games step.
 * Finds opponents all tied teams have played, computes win% against them.
 * Returns no-break if fewer than `threshold` common opponents.
 */
export function createCommonGamesStep<T extends TiebreakerTeam>(
  threshold = 4,
): TiebreakerStepFn<T> {
  return (teams: T[], ctx): StepResult<T> => {
    // Find all opponents each team has played
    const opponentSets = teams.map(t => {
      const games = ctx.games.getGamesForTeam(t.teamAbbreviation);
      const opponents = new Set<string>();
      for (const g of games) {
        if (g.winner !== null) { // only count decided games
          const opp = g.homeTeam === t.teamAbbreviation ? g.awayTeam : g.homeTeam;
          // Exclude teams in the tied group
          if (!teams.some(tied => tied.teamAbbreviation === opp)) {
            opponents.add(opp);
          }
        }
      }
      return opponents;
    });

    // Find common opponents (intersection of all sets)
    let common = new Set(opponentSets[0]);
    for (let i = 1; i < opponentSets.length; i++) {
      common = new Set([...common].filter(opp => opponentSets[i].has(opp)));
    }

    if (common.size < threshold) {
      addExplanationAll(ctx, teams, `Common games: only ${common.size} common opponents (need ${threshold}).`);
      return { winners: [], remaining: teams };
    }

    // Compute win% against common opponents for each team
    const teamPcts = teams.map(t => {
      let w = 0, l = 0, ties = 0;
      const commonGames: Record<string, number[]> = {};
      for (const opp of common) {
        const games = ctx.games.getGamesBetween(t.teamAbbreviation, opp);
        let oppW = 0, oppL = 0, oppTies = 0;
        for (const g of games) {
          if (g.winner === t.teamAbbreviation) {
            w++;
            oppW++;
          }
          else if (g.winner === opp) {
            l++;
            oppL++;
          }
          else {
            ties++;
            oppTies++;
          }
        }
        commonGames[opp] = [oppW, oppL, oppTies];
      }
      const existing = ctx.explainerDetails.get(t.teamAbbreviation) ?? {};
      ctx.explainerDetails.set(t.teamAbbreviation, {
        ...existing,
        CommonGames: commonGames,
      });
      return { team: t, pct: calcPctNaN([w, l, ties]) };
    });

    const best = bestOf(teamPcts, tp => tp.pct);

    if (best.length === teamPcts.length) {
      addExplanationAll(ctx, teams, `Common games: all tied at ${(best[0].pct * 100).toFixed(1)}%.`);
      return { winners: [], remaining: teams };
    }

    const winners = best.map(tp => tp.team);
    const remaining = teamPcts.filter(tp => !best.includes(tp)).map(tp => tp.team);

    for (const tp of teamPcts) {
      addExplanation(ctx, tp.team, `Common games: ${(tp.pct * 100).toFixed(1)}% vs ${common.size} opponents.`);
    }

    if (remaining.length === 0) {
      return { winners, remaining: [], rankMethod: RankMethod.CommonGames };
    }

    return { winners, remaining, rankMethod: RankMethod.CommonGames };
  };
}

export const commonGamesStep = createCommonGamesStep<TiebreakerTeam>();
